import default_avatar from '@/assets/images/default_avatar.png';
import { SelectedMemberInfoType } from '@/components/MemberSelectModal/interface';
import {
  deleteSharing,
  getCollaborators,
  getCollaboratorsAvatar,
  getUserSharingTree,
  putSharing,
} from '@/services/workbench';
import { GetCollaboratorsAvatarRes } from '@/services/workbench/type';
import { useRequest, useToggle } from 'ahooks';
import {
  Avatar,
  Empty,
  Select,
  SelectProps,
  Spin,
  Tag,
  Tooltip,
  message,
} from 'antd';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconFont } from '../IconFont';
import MemberSelectModal from '../MemberSelectModal';
import {
  DepartmentMemberOrgEnum,
  DepartmentType,
  UserType,
} from '../MemberSelectModal/interface';
import SZModal from '../common/SZModal';
import styles from './index.less';
import { AuthorityEnum } from './type';
interface Props {
  visible: boolean;
  info: {
    resource_code: string;
    /**
     * 0 任务
     * 1 模板
     * 2 菜单
     * 3 接口
     * 4 任务批次
     * 5 工作空间
     */
    resource_type: 0 | 1 | 2 | 3 | 4 | 5;
  };
  onCancel: () => void;
  onOk: () => void;
}
const Option = Select.Option;
const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
  event.preventDefault();
  event.stopPropagation();
};
const ShareProjectModal = ({ info, visible, onCancel, onOk }: Props) => {
  const [showMode, { toggle: toggleShowMode }] = useToggle('share', 'manage');
  const [selectModalVisible, { toggle: toggleSelectModalVisible }] =
    useToggle(false);
  const [userList, setUserList] = useState<UserType[]>([]);
  const [departmentList, setDepartmentList] = useState<DepartmentType[]>([]);
  const [options, setOptions] = useState<SelectProps['options']>([]);
  const [loading, setLoading] = useState(false);
  const [selectValue, setSelectValue] = useState<SelectedMemberInfoType[]>([]);
  const [collaborators, setCollaborators] = useState<
    GetCollaboratorsAvatarRes['collaborators']
  >([]);
  const formatOptions = useMemo(() => {
    if (!options) {
      return [];
    }
    return options.map((item) => {
      //判断 collaborators 里面是否有 item.value
      const isExist = collaborators.find(
        (collaborator) => collaborator.uid === item.value,
      );
      if (isExist) {
        item.disabled = true;
      }
      return item;
    });
  }, [options, collaborators]);

  const {
    data: collaboratorList = [],
    run: getCollaboratorsList,
    mutate: mutateCollaboratorsList,
    refresh: refreshCollaboratorsList,
  } = useRequest((params) => getCollaborators({ ...params, ...info }), {
    cacheKey: 'getCollaborators',
    refreshDeps: [showMode],
    onSuccess(data) {
      if (!data) return message.error('获取协作者列表失败');
      return [];
    },
  });

  const { run: deleteSharingMember } = useRequest(deleteSharing, {
    manual: true,
    onSuccess(data) {
      if (data.code !== 0) return message.error(data.msg);
      message.success('删除成功');
    },
    onFinally() {
      getCollaboratorsList(info);
    },
  });

  // 获取协作者头像等信息
  useRequest(() => getCollaboratorsAvatar(info), {
    refreshDeps: [showMode],
    onSuccess(data) {
      if (!data) return message.error('获取协作者失败');
      setCollaborators(data.collaborators);
    },
  });

  const isOkButtonDisable = useMemo(() => {
    return selectValue.length === 0;
  }, [selectValue]);

  const ModalHeader = useMemo(() => {
    if (showMode === 'share') {
      return '选择协作者';
    } else {
      return (
        <div className={styles['modal-header']}>
          <div
            className={styles.left}
            onClick={() => {
              toggleShowMode();
            }}
          >
            <IconFont type="icon-xiangzuoqiehuan" />
            返回
          </div>
          <div className={styles.right}>管理协作者</div>
        </div>
      );
    }
  }, [showMode]);

  const ModalFooter = useMemo(() => {
    if (showMode === 'manage') {
      return (
        <div
          className="text-left text-secondary-1 cursor-pointer"
          onClick={() => {
            toggleSelectModalVisible();
          }}
        >
          <IconFont className="mr-2 text-[15px]" type="icon-yonghu" />
          添加协作者
        </div>
      );
    } else {
      // 返回 null 为不显示 footer
      return undefined;
    }
  }, [showMode]);

  const tagRender = (props: CustomTagProps) => {
    // eslint-disable-next-line
    const { label, value, closable, onClose } = props;

    // TODO: tagRender 如何接收到其他信息？

    const item = selectValue.find((item) => item.value === value);

    return (
      <Tag
        key={value}
        style={{
          marginBottom: '5px',
        }}
        icon={
          <>
            {(item?.type === DepartmentMemberOrgEnum.DEPARTMENT ||
              item?.type === DepartmentMemberOrgEnum.ORG) && (
              <IconFont type="icon-bumen" className="text-xl" />
            )}
            {item?.type === DepartmentMemberOrgEnum.USER && (
              <span className={styles['img-wrapper']}>
                <img
                  loading="lazy"
                  alt="avatar"
                  src={item.avatar || default_avatar}
                />
              </span>
            )}
          </>
        }
        bordered={false}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        className={styles.tag}
      >
        {label}
      </Tag>
    );
  };

  // 获取分享树方法
  //   const getData = async (params: {
  //     resource_code: string;
  //     resource_type?: number;
  //     keyword?: string;
  //     dept_uid?: string;
  //   }) => {
  //     getUserSharingTree(params).then(({ data }) => {
  //       const { dept_infos = [], users = [] } = data || {};
  //
  //       const departmentList = dept_infos.map((item) => {
  //         return {
  //           key: item.dept_uid,
  //           disabled: item.is_bind,
  //           label: item.dept_name,
  //           value: item.dept_uid,
  //           type: DepartmentMemberOrgEnum.DEPARTMENT,
  //         };
  //       });
  //
  //       const userList = users.map((item) => {
  //         return {
  //           key: item.ou_uid,
  //           value: item.ou_uid,
  //           disabled: item.is_bind,
  //           label: item.username,
  //           avatar: item.avatar,
  //           type: DepartmentMemberOrgEnum.USER,
  //         };
  //       });
  //
  //       setUserList(userList);
  //       setDepartmentList(departmentList);
  //       // store.userList = userList;
  //       // store.departmentList = departmentList;
  //     });
  //   };

  // FIXME: 类似方法重复，可以重构
  const handleSearch = useCallback(
    (keyword: string) => {
      setLoading(true);
      getUserSharingTree({
        keyword,
        resource_type: info.resource_type,
        resource_code: info.resource_code,
      })
        .then(({ data }) => {
          const { dept_infos = [], users = [] } = data || {};
          const value1 = dept_infos.map((item) => {
            return {
              key: item.dept_uid,
              label: item.dept_name,
              value: item.dept_uid,
              disabled: item.is_bind,
              type: DepartmentMemberOrgEnum.DEPARTMENT,
            };
          });
          const value2 = users.map((item) => {
            return {
              label: item.username,
              value: item.ou_uid,
              key: item.ou_uid,
              avatar: item?.avatar,
              disabled: item.is_bind,
              type: DepartmentMemberOrgEnum.USER,
            };
          });
          setOptions([...value1, ...value2]);
          setUserList(value2);
          setDepartmentList(value1);
        })
        .finally(() => setLoading(false));
    },
    [info],
  );

  const handleDepartmentChange = useCallback((curDepUid: string) => {
    setLoading(true);
    getUserSharingTree({
      dept_uid: curDepUid,
      resource_type: info.resource_type,
      resource_code: info.resource_code,
    }).then(({ data }) => {
      const { dept_infos = [], users = [] } = data || {};
      const value1 = dept_infos.map((item) => {
        return {
          key: item.dept_uid,
          label: item.dept_name,
          value: item.dept_uid,
          disabled: item.is_bind,
          type: DepartmentMemberOrgEnum.DEPARTMENT,
        };
      });
      const value2 = users.map((item) => {
        return {
          label: item.username,
          value: item.ou_uid,
          key: item.ou_uid,
          avatar: item?.avatar,
          disabled: item.is_bind,
          type: DepartmentMemberOrgEnum.USER,
        };
      });
      setOptions([...value1, ...value2]);
      setUserList(value2);
      setDepartmentList(value1);
      setLoading(false);
    });
  }, []);

  const handleSubmit = useCallback(
    (selectValue: SelectedMemberInfoType[]) => {
      const entities = selectValue.map((item) => {
        return {
          entity_type: item.type,
          entity_uid: item.value,
        };
      });

      putSharing({
        resource_code: info.resource_code,
        resource_type: info.resource_type,
        entities: entities,
        // 430 分享权限从1000-->3000
        // 因为详情页实际上没有做权限控制的；为1000时无法再次分享该任务
        access_level: AuthorityEnum.Manage,
      }).then((res) => {
        if (res.code !== 0) return message.error(res.msg);
        if (showMode === 'manage') {
          refreshCollaboratorsList();
          setSelectValue([]);
        } else {
          onOk();
        }
        message.success('邀请协作者成功');
      });
    },
    [showMode, onOk],
  );

  useEffect(() => {
    handleSearch('');

    // getData({
    //   resource_code: info.resource_code,
    //   resource_type: info.resource_type,
    // });
  }, [showMode, selectModalVisible]);
  return (
    <SZModal
      centered={true}
      title={ModalHeader}
      footer={ModalFooter}
      onCancel={() => {
        onCancel();
      }}
      OKBtnDisable={isOkButtonDisable}
      onOk={debounce(() => handleSubmit(selectValue), 200)}
      open={visible}
    >
      {showMode === 'share' && (
        <div className={styles['share-wrapper']}>
          <div className={styles['item-1']}>
            <div className={styles.header}>
              <span className={styles.label}>邀请协作者</span>
              <div
                onClick={() => {
                  toggleShowMode();
                }}
              >
                <Avatar.Group
                  maxCount={6}
                  maxPopoverTrigger="hover"
                  size="default"
                  maxStyle={{
                    color: '#f56a00',
                    backgroundColor: '#fde3cf',
                    cursor: 'pointer',
                  }}
                >
                  {collaborators?.map((item) => {
                    return (
                      <Tooltip key={item.uid} title={item.name} placement="top">
                        <Avatar key={item.uid} src={item?.avatar} />
                      </Tooltip>
                    );
                  })}
                </Avatar.Group>
              </div>
            </div>
            <div className={styles.body}>
              <div className={styles.input}>
                <div
                  className={classNames(
                    styles.left,
                    styles.collaboratorScroller,
                  )}
                >
                  <Select
                    suffixIcon={null}
                    variant="borderless"
                    notFoundContent={
                      loading ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px',
                          }}
                        >
                          <Spin size="small" />
                        </div>
                      ) : (
                        <Empty />
                      )
                    }
                    mode="multiple"
                    optionFilterProp="label"
                    tagRender={tagRender}
                    style={{ width: '100%' }}
                    placeholder="搜索用户或部门"
                    value={selectValue}
                    onSelect={(val, fullVal) => {
                      const list = [...selectValue, fullVal];
                      // @ts-ignore
                      setSelectValue(list);
                    }}
                    onSearch={handleSearch}
                    // options={options}
                    onDeselect={(val) => {
                      setSelectValue((prev) => {
                        // @ts-ignore
                        return prev?.filter((item) => item.value !== val);
                      });
                    }}
                    popupClassName="!absolute !top-2 "
                    getPopupContainer={() => {
                      return document.querySelector(
                        '#dropdown-container',
                      ) as HTMLElement;
                    }}
                  >
                    {formatOptions.map((item) => {
                      return (
                        <Option
                          key={item.value}
                          value={item.value}
                          label={item.label}
                          avatar={item?.avatar}
                          type={item?.type}
                          disabled={item?.disabled}
                          style={{
                            contentVisibility: 'auto',
                          }}
                        >
                          {(item?.type === DepartmentMemberOrgEnum.DEPARTMENT ||
                            item?.type === DepartmentMemberOrgEnum.ORG) && (
                            <IconFont
                              type="icon-bumen"
                              style={{
                                fontSize: '26px',
                                marginRight: '10px',
                                verticalAlign: 'middle',
                              }}
                            />
                          )}
                          {item?.type === DepartmentMemberOrgEnum.USER && (
                            <Avatar
                              style={{
                                height: '27px',
                                width: '27px',
                                marginRight: '10px',
                              }}
                              src={item?.avatar}
                            />
                          )}
                          {item.label}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
                <div
                  className={styles.right}
                  onClick={() => {
                    toggleSelectModalVisible();
                  }}
                >
                  <IconFont type="icon-yonghu" className={styles['add-btn']} />
                </div>
              </div>
              {/* PopupContainer */}
              <div className="relative" id="dropdown-container"></div>
            </div>
          </div>
        </div>
      )}
      {showMode === 'manage' && (
        <div className={styles['manage-wrapper']}>
          {collaboratorList?.map((item) => {
            return (
              <div className={styles['user-wrapper']} key={item.uid}>
                <div className={styles['left']}>
                  <figure className={styles['img-wrapper']}>
                    {item.entity_type === DepartmentMemberOrgEnum.DEPARTMENT ||
                    item.entity_type === DepartmentMemberOrgEnum.ORG ? (
                      <IconFont type="icon-bumen" className="text-[46px]" />
                    ) : (
                      <img
                        src={item.avatar || default_avatar}
                        alt="avatar"
                        loading="lazy"
                      />
                    )}
                  </figure>
                  <section className={styles.info}>
                    <div className={styles.header}>
                      <h2>{item.name}</h2>
                      {item.is_owner && (
                        <span className={styles['owner-tag']}>所有者</span>
                      )}
                    </div>
                    <p>{item?.complete_name}</p>
                  </section>
                </div>
                <div className={styles.right}>
                  <Select
                    defaultValue={item.access_level || 1000}
                    popupClassName={styles['popup-container']}
                    value={item.access_level || 1000}
                    variant="borderless"
                    onChange={(value: AuthorityEnum) => {
                      // Manage 相当于删除
                      if (value === AuthorityEnum.Remove) {
                        mutateCollaboratorsList(
                          collaboratorList?.filter(
                            (collaborator) => collaborator.uid !== item.uid,
                          ),
                        );
                        deleteSharingMember({
                          access_level: item.access_level,
                          resource_code: info.resource_code,
                          resource_type: info.resource_type,
                          entities: [
                            {
                              entity_type: item.entity_type,
                              entity_uid: item.uid,
                            },
                          ],
                        });
                      } else {
                        putSharing({
                          resource_code: info.resource_code,
                          resource_type: info.resource_type,
                          entities: [
                            {
                              entity_type: item.entity_type,
                              entity_uid: item.uid,
                            },
                          ],
                          access_level: value,
                        }).then((res) => {
                          const { code } = res;
                          if (code === 0) {
                            refreshCollaboratorsList();
                          }
                          message.success('权限修改成功');
                        });
                      }
                    }}
                    options={[
                      {
                        key: AuthorityEnum.Manage,
                        value: AuthorityEnum.Manage,
                        label: '可管理',
                      },
                      {
                        key: AuthorityEnum.Write,
                        value: AuthorityEnum.Write,
                        label: '可编辑',
                        disabled: !!item.is_owner,
                      },
                      {
                        key: AuthorityEnum.Read,
                        value: AuthorityEnum.Read,
                        label: '可查看',
                        disabled: !!item.is_owner,
                      },
                      {
                        key: AuthorityEnum.Remove,
                        value: AuthorityEnum.Remove,
                        label: '移除',
                        disabled: item.is_owner ? true : false,
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
          {collaboratorList?.length === 0 && <Empty />}
        </div>
      )}
      {selectModalVisible && (
        <MemberSelectModal
          userList={userList}
          departmentList={departmentList}
          visible={selectModalVisible}
          defaultCheckedList={selectValue}
          onDepartmentChange={handleDepartmentChange}
          onSearch={handleSearch}
          loading={loading}
          onOk={(val) => {
            if (showMode === 'manage') {
              handleSubmit(val);
            } else if (showMode === 'share') {
              setSelectValue(val);
            }
            toggleSelectModalVisible();
            handleSearch('');
          }}
          onClose={() => {
            toggleSelectModalVisible();
          }}
        />
      )}
    </SZModal>
  );
};

export default ShareProjectModal;
